import React from "react";
import { SERVICES } from "../data/content";
import { Tag } from "../components/ui-primitives";
import ClosingCTA from "../components/ClosingCTA";
import VerticalTabs from "../components/VerticalTabs";

const PROCESS = [
  {
    n: "01",
    title: "Plan",
    body: "Audience profiling, market selection, channel mix recommendation, and media schedule. Documented reasoning — not instinct.",
  },
  {
    n: "02",
    title: "Make",
    body: "In-house creative production — briefs, design, copy, and campaign collateral handled within the same team, the same timeline, and the same accountability as the media plan. No handoffs to a separate agency.",
  },
  {
    n: "03",
    title: "Place",
    body: "Negotiation, booking, and deployment. Annual deal structures. Rate advantages that single-advertiser direct buying cannot match.",
  },
  {
    n: "04",
    title: "Measure",
    body: "MIS confirmation of every placement by 10:00 AM on the day of publication. Post-campaign performance reporting and audit trails. Full transparency on every rupee spent.",
  },
];

const MEDIA_PARTNERS = [
  {
    id: "newspapers",
    label: "Newspapers",
    items: [
      "Times of India",
      "Dainik Jagran",
      "Hindustan Times",
      "DB Corp",
      "Amar Ujala",
      "Rajasthan Patrika",
      "The Hindu",
      "Ananda Bazar Patrika",
      "Eenadu",
      "Lokmat",
    ],
  },
  {
    id: "magazines",
    label: "Magazines & Inflight",
    items: [
      "Grihshobha",
      "Forbes India",
      "Meri Saheli",
      "Vanitha",
      "Frontline",
      "Hello 6E",
    ],
  },
  {
    id: "television",
    label: "Television",
    items: ["Aaj Tak", "News18", "ABP News", "NDTV", "Jio News", "India TV"],
  },
  {
    id: "radio",
    label: "Radio",
    items: [
      "Radio Mirchi 98.3",
      "Red FM",
      "Radio City 91.1",
      "My FM 94.3",
      "Big FM",
      "Fever FM",
    ],
  },
  {
    id: "cinema-ooh",
    label: "Cinema & OOH",
    items: [
      "National Advertising Agency",
      "Decode Advertising",
      "Qube Cinema",
      "TIM Delhi Airport",
      "Blue Box Media",
    ],
  },
  {
    id: "digital",
    label: "Digital",
    items: ["Times Internet Limited", "HT Digital Streams", "ABP Digital"],
  },
];

// Red-dot bulleted list used inside the service tab panels.
function BulletList({ items }) {
  return (
    <ul style={{ listStyle: "none", padding: 0, margin: 0 }} className="space-y-3">
      {items.map((b, i) => (
        <li
          key={typeof b === "string" ? b : `bullet-${i}`}
          className="flex items-start gap-3 body-md"
          style={{ color: "var(--color-on-surface-variant)" }}
        >
          <span
            aria-hidden
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "var(--color-tertiary)",
              display: "inline-block",
              flexShrink: 0,
              marginTop: 10,
            }}
          />
          <span>{b}</span>
        </li>
      ))}
    </ul>
  );
}

// Render service bullets with the inline Transmitter link on the Creative tile.
function renderBulletsFor(service) {
  if (service.id === "creative") {
    return service.bullets.map((b, i) => {
      if (i !== 0 || !b.includes("Transmitter")) return b;
      const [before, after] = b.split("Transmitter");
      return (
        <React.Fragment key={`creative-bullet-${i}`}>
          {before}
          <a
            href="https://transmitteragency.com/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-creative-transmitter-link"
            style={{
              color: "var(--color-tertiary)",
              textDecoration: "underline",
              textUnderlineOffset: 3,
            }}
          >
            Transmitter
          </a>
          {after}
        </React.Fragment>
      );
    });
  }
  return service.bullets;
}

// Panel rendered inside the services VerticalTabs.
// The expanded paragraph is hidden behind a "Read more" toggle so the
// default panel state stays compact (bullets only). Each ServicePanel
// owns its own toggle state — switching tabs preserves it on desktop,
// resets it on mobile (where panels unmount when inactive).
function ServicePanel({ service }) {
  const [expanded, setExpanded] = React.useState(false);
  const isGov = service.id === "government";
  const hasMore = Boolean(service.expanded);

  return (
    <article data-testid={`services-panel-${service.id}`}>
      <BulletList items={renderBulletsFor(service)} />

      {hasMore && (
        <>
          <button
            type="button"
            data-testid={`services-panel-toggle-${service.id}`}
            aria-expanded={expanded}
            onClick={() => setExpanded((v) => !v)}
            className="services-panel-toggle"
            style={{
              marginTop: 18,
              padding: 0,
              background: "transparent",
              border: "none",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              gap: 10,
              color: "var(--color-tertiary)",
              fontFamily: "var(--font-body)",
              fontWeight: 700,
              fontSize: "0.78rem",
              letterSpacing: "0.12em",
              textTransform: "uppercase",
            }}
          >
            <span
              aria-hidden
              style={{
                fontFamily: "var(--font-display)",
                fontWeight: 700,
                fontSize: "1.05em",
                letterSpacing: "-0.04em",
                display: "inline-block",
                transform: expanded ? "rotate(90deg)" : "rotate(0deg)",
                transition: "transform 0.22s ease",
              }}
            >
              »
            </span>
            {expanded ? "Read less" : "Read more"}
          </button>

          <div
            data-testid={`services-panel-expand-${service.id}`}
            style={{
              display: "grid",
              gridTemplateRows: expanded ? "1fr" : "0fr",
              transition: "grid-template-rows 0.28s ease",
            }}
          >
            <div style={{ overflow: "hidden" }}>
              <p
                className="body-sm"
                style={{
                  color: "var(--color-on-surface)",
                  marginTop: expanded ? 16 : 0,
                  paddingTop: 14,
                  borderTop: "1px solid var(--color-outline)",
                  lineHeight: 1.6,
                }}
              >
                {isGov ? (
                  <>
                    Our strategic partnership with{" "}
                    <a
                      href="https://nationalads.in/"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="services-gov-naa-link"
                      style={{
                        color: "var(--color-tertiary)",
                        textDecoration: "underline",
                        textUnderlineOffset: 3,
                      }}
                    >
                      National Advertising Agency
                    </a>{" "}
                    extends our government advertising reach beyond DAVP, UP,
                    and Rajasthan — bringing over 100 years of specialised
                    government media expertise to every mandate we handle.
                  </>
                ) : (
                  service.expanded
                )}
              </p>
            </div>
          </div>
        </>
      )}
    </article>
  );
}

// Panel rendered inside the media partners VerticalTabs.
function MediaPartnerPanel({ group }) {
  return (
    <div
      data-testid={`media-partner-panel-${group.id}`}
      style={{ display: "flex", flexDirection: "column", gap: 14 }}
    >
      {group.items.map((name) => (
        <div
          key={name}
          className="flex items-center gap-3 body-md"
          style={{ color: "var(--color-on-surface)" }}
        >
          <span
            aria-hidden
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "var(--color-tertiary)",
              display: "inline-block",
              flexShrink: 0,
            }}
          />
          <span>{name}</span>
        </div>
      ))}
      {/* Italic "and more" bullet at the end of each panel — signals the list
          is illustrative, not exhaustive, without bloating each category. */}
      <div
        data-testid={`media-partner-panel-${group.id}-andmore`}
        className="flex items-center gap-3 body-md"
        style={{
          color: "var(--color-on-surface-variant)",
          fontStyle: "italic",
        }}
      >
        <span
          aria-hidden
          style={{
            width: 5,
            height: 5,
            borderRadius: "50%",
            background: "var(--color-tertiary)",
            display: "inline-block",
            flexShrink: 0,
            opacity: 0.6,
          }}
        />
        <span>and more</span>
      </div>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <main data-testid="services-page" style={{ background: "var(--color-surface)" }}>
      <section style={{ paddingTop: 152, paddingBottom: 64 }}>
        <div className="di-container">
          <Tag>What We Do</Tag>
          <h1 className="display-lg mt-6" style={{ maxWidth: 1100 }}>
            Advertising: planned, placed,
            <br />
            measured, and made.
          </h1>
          <p
            className="mt-8"
            style={{ color: "var(--color-on-surface-variant)", maxWidth: 720 }}
          >
            We have been advertising brands across Bharat since 2008, building
            over 1,000 media partnerships — from the national names you know
            to the regional vernacular properties your audience prefers in
            their language, in their city.
          </p>
          <p
            className="mt-5"
            style={{ color: "var(--color-on-surface)", maxWidth: 720 }}
          >
            <strong style={{ color: "var(--color-on-surface)", fontWeight: 700 }}>
              The access we offer isn't on any rate card.
            </strong>
          </p>
        </div>
      </section>

      <section
        className="di-section"
        style={{ background: "var(--color-surface-container-low)" }}
      >
        <div className="di-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-10">
            <div className="lg:col-span-6">
              <h2 className="display-md">One call covers it all.</h2>
            </div>
            <div className="lg:col-span-5 lg:col-start-8 lg:pt-2">
              <p style={{ color: "var(--color-on-surface-variant)" }}>
                Strategy, creative, placement, and measurement — all of it,
                without briefing four different agencies.
              </p>
            </div>
          </div>

          <div data-testid="services-vertical-tabs">
            <VerticalTabs
              testIdPrefix="services"
              defaultId={SERVICES[0]?.id}
              alignContent="center"
              tabs={SERVICES.map((s) => ({
                id: s.id,
                label: s.name,
                render: () => <ServicePanel service={s} />,
              }))}
            />
          </div>
        </div>
      </section>

      {/* How we work — navy */}
      <section
        className="di-section"
        style={{ background: "var(--color-primary)", color: "var(--color-on-primary)" }}
      >
        <div className="di-container">
          <Tag light>How We Work</Tag>
          <h2
            className="display-md mt-6 mb-12"
            style={{ color: "var(--color-on-primary)" }}
          >
            Four steps. No ceremony.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-2">
            {PROCESS.map((p) => (
              <div
                key={p.n}
                data-testid={`process-step-${p.n}`}
                style={{
                  background: "rgba(249,249,248,0.04)",
                  padding: "28px",
                }}
              >
                <div
                  className="display-md"
                  style={{
                    color: "rgba(249,249,248,0.1)",
                    fontWeight: 800,
                    lineHeight: 1,
                    marginBottom: 16,
                  }}
                >
                  {p.n}
                </div>
                <h3
                  className="headline-lg mb-4"
                  style={{ color: "var(--color-on-primary)" }}
                >
                  {p.title}
                </h3>
                <p
                  className="body-md"
                  style={{ color: "rgba(249,249,248,0.7)" }}
                >
                  {p.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Media Partner Relationships — light stone surface to break the
          two consecutive navy sections (HOW WE WORK and closing CTA). */}
      <section
        data-testid="services-media-partnerships"
        className="di-section"
        style={{ background: "var(--color-surface)" }}
      >
        <div className="di-container">
          <Tag>Media Partnerships</Tag>
          <h2 className="display-md mt-6 mb-10" style={{ maxWidth: 900 }}>
            1,000+ partnerships. Built over 17 years.
          </h2>

          <div data-testid="media-partners-vertical-tabs">
            <VerticalTabs
              testIdPrefix="media-partners"
              defaultId={MEDIA_PARTNERS[0]?.id}
              alignContent="center"
              tabs={MEDIA_PARTNERS.map((g) => ({
                id: g.id,
                label: g.label,
                render: () => <MediaPartnerPanel group={g} />,
              }))}
            />
          </div>

          <p
            className="body-md mt-12"
            style={{
              color: "var(--color-on-surface-variant)",
              fontStyle: "italic",
            }}
          >
            Plus 800+ regional and vernacular properties across 18 languages.
          </p>
        </div>
      </section>

      <ClosingCTA />
    </main>
  );
}
